import { isWithinRange } from './dateRangeFilter.js';

/**
 * Agrega estatísticas da comunidade a partir dos documentos crus de
 * inventoryItems, events e recipes. Lógica pura (sem Firestore) para ser
 * testável e para permitir recomputar ao mudar o filtro de datas sem refetch.
 *
 * Cada item deve ter ao menos `createdAt` (Date/Timestamp/string) e `userId`.
 * O filtro de datas [from, to] (YYYY-MM-DD) é aplicado por `createdAt`.
 */
export function computeCommunityStats({ inventory = [], events = [], recipes = [], from = '', to = '' } = {}) {
  const inv = inventory.filter((d) => isWithinRange(d.createdAt, from, to));
  const evs = events.filter((d) => isWithinRange(d.createdAt, from, to));
  const recs = recipes.filter((d) => isWithinRange(d.createdAt, from, to));

  const userIds = new Set();
  const categoryMap = new Map();
  const wordMap = new Map();
  const inventoryItemsMap = new Map();

  inv.forEach((data) => {
    inventoryItemsMap.set(data.id, {
      name: data.itemName || 'Sem nome',
      category: data.category || 'Sem categoria',
    });
    if (data.userId) userIds.add(data.userId);

    const category = data.category || 'Sem categoria';
    categoryMap.set(category, (categoryMap.get(category) || 0) + 1);

    const itemName = (data.itemName || '').toLowerCase();
    const words = itemName.split(/\s+/).filter((w) => w.length > 2);
    words.forEach((word) => {
      const cleaned = word.replace(/[^a-záàâãéèêíïóôõöúçñ]/gi, '');
      if (cleaned.length > 2) {
        wordMap.set(cleaned, (wordMap.get(cleaned) || 0) + 1);
      }
    });
  });

  const recipesMap = new Map();
  recs.forEach((data) => {
    recipesMap.set(data.id, data.title || 'Sem nome');
    if (data.userId) userIds.add(data.userId);
  });

  const itemUsageMap = new Map();
  const recipeUsageMap = new Map();
  evs.forEach((data) => {
    if (data.userId) userIds.add(data.userId);

    if (Array.isArray(data.inventoryItems)) {
      data.inventoryItems.forEach((itemId) => {
        const item = inventoryItemsMap.get(itemId);
        if (item) {
          const existing = itemUsageMap.get(itemId);
          if (existing) existing.count++;
          else itemUsageMap.set(itemId, { name: item.name, category: item.category, count: 1 });
        }
      });
    }

    if (Array.isArray(data.recipes)) {
      data.recipes.forEach((recipeId) => {
        const recipeName = recipesMap.get(recipeId);
        if (recipeName) {
          const existing = recipeUsageMap.get(recipeId);
          if (existing) existing.count++;
          else recipeUsageMap.set(recipeId, { name: recipeName, count: 1 });
        }
      });
    }
  });

  const totalUsers = userIds.size;
  const totalInventory = inv.length;
  const totalEvents = evs.length;
  const totalRecipes = recs.length;

  const categoryStats = Array.from(categoryMap.entries())
    .map(([category, count]) => ({ category, count }))
    .sort((a, b) => b.count - a.count);

  const wordStats = Array.from(wordMap.entries())
    .map(([word, count]) => ({ word, count }))
    .filter((w) => w.count >= 3)
    .sort((a, b) => b.count - a.count)
    .slice(0, 20);

  const topInventoryItems = Array.from(itemUsageMap.values())
    .map((item) => ({ itemName: item.name, usageCount: item.count, category: item.category }))
    .sort((a, b) => b.usageCount - a.usageCount)
    .slice(0, 20);

  const topRecipes = Array.from(recipeUsageMap.values())
    .map((recipe) => ({ recipeName: recipe.name, usageCount: recipe.count }))
    .sort((a, b) => b.usageCount - a.usageCount)
    .slice(0, 20);

  return {
    totalInventory,
    totalEvents,
    totalRecipes,
    avgInventoryPerUser: totalUsers > 0 ? totalInventory / totalUsers : 0,
    avgEventsPerUser: totalUsers > 0 ? totalEvents / totalUsers : 0,
    avgRecipesPerUser: totalUsers > 0 ? totalRecipes / totalUsers : 0,
    categoryStats,
    wordStats,
    topInventoryItems,
    topRecipes,
  };
}
